import { useEffect, useRef } from 'react';

const AboutSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const highlights = [
    { icon: 'ri-award-line', text: 'Más de 5 años de experiencia en el sector' },
    { icon: 'ri-shield-check-line', text: 'Equipo técnico certificado y profesional' },
    { icon: 'ri-map-pin-line', text: 'Cobertura en toda la república mexicana' },
  ];

  return (
    <section id="about" className="relative bg-[#0a0a0a] py-24 md:py-32 overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: 'radial-gradient(circle, #06b6d4 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>
      {/* Cyan glow */}
      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-cyan-500/5 blur-3xl pointer-events-none" />

      <div ref={sectionRef} className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Content */}
          <div className="flex flex-col">
            <div
              className="reveal inline-flex items-center gap-2 border border-cyan-500/40 bg-cyan-500/10 text-cyan-400 text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-6 self-start"
              style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
            >
              Sobre Nosotros
            </div>

            <h2
              className="reveal text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6"
              style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
            >
              Líderes en{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-sky-500">
                tecnología visual
              </span>{' '}
              para expos y eventos
            </h2>

            <p
              className="reveal text-gray-400 text-base md:text-lg leading-relaxed mb-4"
              style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
            >
              En <strong className="text-cyan-400">Astro Screen</strong> transformamos expos, ferias y eventos en experiencias visuales extraordinarias. Somos especialistas en renta de pantallas LED de alta resolución para todo tipo de eventos.
            </p>

            <p
              className="reveal text-gray-400 text-base md:text-lg leading-relaxed mb-8"
              style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
            >
              Desde exposiciones masivas hasta conferencias corporativas, nuestro equipo garantiza una instalación impecable, soporte técnico en tiempo real y resultados que superan expectativas.
            </p>

            <div className="flex flex-col gap-4">
              {highlights.map((item) => (
                <div
                  key={item.text}
                  className="reveal flex items-center gap-4"
                  style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-cyan-500/15 border border-cyan-500/30 flex-shrink-0">
                    <i className={`${item.icon} text-cyan-400 text-lg`} />
                  </div>
                  <span className="text-gray-300 text-sm md:text-base">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Image */}
          <div
            className="reveal relative"
            style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
          >
            <div className="relative rounded-2xl overflow-hidden group">
              <img
                src="https://storage.readdy-site.link/project_files/06aadc1d-6920-43e8-b307-dffb1d65b532/03705b76-babe-4524-8d81-3e47574029e5_WhatsApp-Image-2026-06-09-at-12.52.32-1.jpeg?v=1a806b64fdcdf46cd0b7323f55b51df0"
                alt="Equipo Astro Screen instalando pantallas LED en expo"
                className="w-full h-[400px] md:h-[520px] object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>

            {/* Floating badge */}
            <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-cyan-500 to-sky-600 text-white rounded-2xl p-5 shadow-2xl">
              <div className="text-3xl font-extrabold">500+</div>
              <div className="text-xs font-semibold uppercase tracking-wide mt-1">Eventos exitosos</div>
            </div>

            {/* Glow effect */}
            <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-cyan-500/20 to-sky-500/10 blur-xl -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
